import {
  Grid,
  Hidden,
  IconButton,
  makeStyles,
  MenuItem,
  Portal,
  SvgIcon,
  Typography,
} from "@material-ui/core";
import Menu from "@material-ui/core/Menu";
import Slider from "@material-ui/core/Slider";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import PauseIcon from "@material-ui/icons/Pause";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import VolumeOffIcon from "@material-ui/icons/VolumeOff";
import VolumeUpIcon from "@material-ui/icons/VolumeUp";
import React, { useCallback, useEffect, useState } from "react";

const useStyles = makeStyles((theme) => ({
  controller: {
    bottom: "55px",
    position: "relative",
    padding: "1%",
  },
  btnStyle: {
    color: theme.palette.error.main,
  },
  slider: { width: "15%" },
  progress: {
    padding: "0 1%",
    width: "auto",
  },
  duration: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  speedButton: {
    color: theme.palette.error.main,
    marginLeft: theme.spacing(1),
  },
  speedActive: {
    color: theme.palette.error.light,
  },
  speedMenu: {
    "& .MuiList-root": {
      backgroundColor: "#000",
      color: "#fff",
    },
    "& .MuiMenuItem-root": {
      color: "#fff",
      "&.Mui-selected": {
        backgroundColor: "rgba(255, 255, 255, 0.2)",
      },
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.1)",
      },
    },
  },
  fullscreenSpeedMenu: {
    "& .MuiPopover-paper": {
      backgroundColor: "#000 !important",
      color: "#fff",
    },
  },
  menuItem: {
    minHeight: "32px",
    fontSize: "14px",
  },
  seekOverlay: {
    position: "absolute",
    top: 0,
    bottom: 0,
    width: "50%",
    height: "100%",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.1)",
    opacity: 0,
    transition: "opacity 0.2s",
    "&:hover": {
      opacity: 1,
    },
  },
  seekLeft: {
    left: 0,
  },
  seekRight: {
    right: 0,
  },
}));

// formate video duration as per requirement
function fmtMSS(seconds) {
  const format = (val) => `0${Math.floor(val)}`.slice(-2);
  const hours = seconds / 3600;
  const minutes = (seconds % 3600) / 60;
  return [hours, minutes, seconds % 60].map(format).join(":");
}

const CustomSpeedIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <circle
      cx="12"
      cy="12"
      r="10"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    />
    <text
      x="12"
      y="14"
      textAnchor="middle"
      fontSize="8"
      fill="currentColor"
      style={{ fontWeight: "bold" }}
    >
      {props.speed}x
    </text>
  </SvgIcon>
);

const YoutubeControllers = ({ play, playerStateChange, id = "" }) => {
  const agent = navigator.userAgent;
  const classes = useStyles();
  const [progressBar, setProgressBar] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [value, setValue] = useState(
    play.getVolume && play.getVolume() ? play.getVolume() : 100
  );
  const [isMute, setIsMute] = useState(
    play.isMuted && play.isMuted() ? play.isMuted() : false
  );
  const [speedAnchorEl, setSpeedAnchorEl] = useState(null);
  const [currentSpeed, setCurrentSpeed] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // get video's total duration from youtube API
  const duration = useCallback(() => {
    playerStateChange.getDuration &&
      setVideoDuration(fmtMSS(playerStateChange.getDuration()));
  }, [playerStateChange]);
  // handle volume change of player
  const handleChange = (event, newValue) => {
    setValue(newValue);
    play.setVolume(newValue);
  };
  useEffect(() => {
    // get current status of video
    if (playerStateChange.getDuration) {
      const playerTotalTime = playerStateChange.getDuration();
      var playerCurrentTime = playerStateChange.getCurrentTime();

      var playerTimeDifference = (playerCurrentTime / playerTotalTime) * 100;
      setProgressBar(playerTimeDifference);
      duration();
    }
  });

  // slider for navigation through video
  const handleSeek = (event, newValue) => {
    if (playerStateChange.getDuration) {
      const totalTime = playerStateChange.getDuration();
      const seekValue = totalTime * (newValue / 100);
      play.seekTo(seekValue, true);
      setProgressBar(seekValue);
    }
  };

  // full screen handler
  const handleFullScreen = () => {
    play.playVideo();
    let isInFullScreen =
      (document.fullscreenElement && document.fullscreenElement !== null) ||
      (document.webkitFullscreenElement &&
        document.webkitFullscreenElement !== null) ||
      (document.mozFullScreenElement &&
        document.mozFullScreenElement !== null) ||
      (document.msFullscreenElement && document.msFullscreenElement !== null);

    setIsFullscreen(!isInFullScreen);
    const iframeDIV = document.getElementById("yt");

    let requestFullScreen =
      iframeDIV.requestFullScreen ||
      iframeDIV.mozRequestFullScreen ||
      iframeDIV.webkitRequestFullScreen;

    let cancelFullScreen =
      document.exitFullscreen ||
      document.mozCancelFullScreen ||
      document.webkitExitFullscreen ||
      document.msExitFullscreen;

    if (!isInFullScreen) {
      if (requestFullScreen) {
        requestFullScreen.bind(iframeDIV)();
        // switch to full screen and change height and width of iframe container
        document.getElementById("youtube").style.width = "100vw";
        document.getElementById("youtube").style.height = "100vh";
      }
    } else {
      cancelFullScreen.call(document);
      // if exiting full screen switch width and height to default value
      // To be handled manually as youtube api doesn't exposes full screen handlers
      document.getElementById("youtube").style.width = "100%";
      document.getElementById("youtube").style.height = "400px";
    }
  };

  const handleSpeedClick = (event) => {
    setSpeedAnchorEl(event.currentTarget);
    // Add keyboard listener
    document.addEventListener("keydown", handleSpeedKeyPress);
  };

  const handleSpeedClose = (speed) => {
    setSpeedAnchorEl(null);
    // Remove keyboard listener
    document.removeEventListener("keydown", handleSpeedKeyPress);
    if (typeof speed === "number") {
      setCurrentSpeed(speed);
      play.setPlaybackRate(speed);
    }
  };

  const handleSpeedKeyPress = (event) => {
    if (!speedAnchorEl) return;

    const currentIndex = speedOptions.indexOf(currentSpeed);
    let newSpeed = currentSpeed;

    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (currentIndex < speedOptions.length - 1) {
        newSpeed = speedOptions[currentIndex + 1];
      }
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      if (currentIndex > 0) {
        newSpeed = speedOptions[currentIndex - 1];
      }
    }

    if (newSpeed !== currentSpeed) {
      setCurrentSpeed(newSpeed);
      play.setPlaybackRate(newSpeed);
    }
  };

  const speedOptions = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

  const handleDoubleClick = (direction) => {
    if (playerStateChange.getCurrentTime) {
      const currentTime = playerStateChange.getCurrentTime();
      const newTime =
        direction === "forward" ? currentTime + 10 : currentTime - 10;
      play.seekTo(newTime, true);
    }
  };

  useEffect(() => {
    // Add keyboard listener for seeking
    document.addEventListener("keydown", handleSeekKeyPress);
    return () => {
      document.removeEventListener("keydown", handleSeekKeyPress);
    };
  }, []);

  const handleSeekKeyPress = (event) => {
    if (playerStateChange.getCurrentTime) {
      const currentTime = playerStateChange.getCurrentTime();

      if (event.key === "ArrowRight") {
        event.preventDefault();
        play.seekTo(currentTime + 10, true);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        play.seekTo(currentTime - 10, true);
      }
    }
  };

  return (
    <>
      {/* Add these divs before the controls */}
      <div
        className={`${classes.seekOverlay} ${classes.seekLeft}`}
        onDoubleClick={() => handleDoubleClick("backward")}
      />
      <div
        className={`${classes.seekOverlay} ${classes.seekRight}`}
        onDoubleClick={() => handleDoubleClick("forward")}
      />
      <Grid container direction="column" className={classes.controller}>
        {/* Progress bar */}
        <Slider
          component="span"
          color="secondary"
          classes={{ root: classes.progress }}
          value={progressBar}
          onChange={handleSeek}
          aria-labelledby="continuous-slider"
        />
        <Grid item container alignItems="center">
          <Grid item xs={6}>
            <Grid item container direction="row">
              {/* play/pause controllers  */}
              {play.getPlayerState && play.getPlayerState() === 1 ? (
                <IconButton
                  id="play-button"
                  className={classes.btnStyle}
                  size="small"
                  onClick={() => {
                    play.pauseVideo();
                    if (agent.includes("Android")) {
                      if (window.Android) window.Android.playYoutubeVideo(id);
                    }
                  }}
                >
                  <PauseIcon />
                </IconButton>
              ) : (
                <IconButton
                  id="play-button"
                  className={classes.btnStyle}
                  size="small"
                  onClick={() => {
                    play.playVideo();
                    if (agent.includes("Android")) {
                      if (window.Android) window.Android.playYoutubeVideo(id);
                    }
                  }}
                >
                  <PlayArrowIcon />
                </IconButton>
              )}
              &emsp;
              {/* mute/unmute controllers  */}
              <IconButton
                className={classes.btnStyle}
                size="small"
                onClick={() => {
                  setIsMute(!isMute);
                  isMute ? play.unMute() : play.mute();
                }}
              >
                {isMute ? <VolumeOffIcon /> : <VolumeUpIcon />}
              </IconButton>
              &emsp;
              {/* volume controller  */}
              <Hidden smDown>
                <Slider
                  component="span"
                  value={value}
                  color="secondary"
                  classes={{ root: classes.slider }}
                  onChange={handleChange}
                  aria-labelledby="continuous-slider"
                />
                &nbsp;&nbsp;&nbsp;
              </Hidden>
              {/* duration display */}
              <Typography
                className={classes.duration}
                variant="caption"
                color="error"
              >
                {playerStateChange.getDuration &&
                  `${fmtMSS(
                    playerStateChange.getCurrentTime()
                  )}/${videoDuration}`}
              </Typography>
            </Grid>
          </Grid>

          <Grid item xs={6} container direction="row" justify="flex-end">
            {/* full screen controller  */}
            <IconButton
              className={`${classes.speedButton} ${
                currentSpeed !== 1 ? classes.speedActive : ""
              }`}
              size="small"
              onClick={handleSpeedClick}
            >
              <CustomSpeedIcon speed={currentSpeed} />
            </IconButton>
            <Portal container={document.body}>
              <Menu
                id="speed-menu"
                anchorEl={speedAnchorEl}
                keepMounted
                open={Boolean(speedAnchorEl)}
                onClose={() => handleSpeedClose()}
                className={
                  isFullscreen ? classes.fullscreenSpeedMenu : classes.speedMenu
                }
                anchorReference="anchorPosition"
                anchorPosition={
                  speedAnchorEl
                    ? {
                        top: speedAnchorEl.getBoundingClientRect().bottom,
                        left: speedAnchorEl.getBoundingClientRect().left,
                      }
                    : undefined
                }
                transformOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
                style={{
                  zIndex: 9999999999,
                }}
              >
                {speedOptions.map((speed) => (
                  <MenuItem
                    key={speed}
                    selected={currentSpeed === speed}
                    onClick={() => handleSpeedClose(speed)}
                    className={classes.menuItem}
                    style={{ color: "#fff" }}
                  >
                    {speed}x
                  </MenuItem>
                ))}
              </Menu>
            </Portal>
            <IconButton
              className={classes.btnStyle}
              size="small"
              onClick={handleFullScreen}
            >
              <FullscreenIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default YoutubeControllers;
